import React, {useEffect, useState} from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {Grid, Typography, useTheme} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {InstalledBaseNode, InstalledBaseNodeTypeKey} from "../../codegen/graphql";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {toast} from "react-toastify";
import Box from "@mui/material/Box";
import {Button, CircularProgress, TextField, ToggleButton, ToggleButtonGroup} from '@mui/material';
import {useNavigate} from "react-router-dom";
import {UpdateSystem} from "../../graphql/mutations/updateSystem";
import { GATEWAY_SYSTEM_TYPES } from "../../config/systemTypes";
import {SystemNameplateInfo} from "../../graphql/queries/systemNameplateInfo";
import {SystemStructureCounts} from "../../graphql/queries/systemStructureCounts";
import {getChildRule} from "../../utils/nodeChildRules";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {useIotAttachGate} from "../../hooks/useIotAttachGate";
import NetworkScanGateReason from "../NetworkScanGateReason";

interface Props {
    node: InstalledBaseNode | { id: string; title: string; system?: { id: string } | null };
    refetchFunction?: () => void;
    open: boolean,
    onClose?: () => void,
}


const IotSystemRegistrationModal = ({node, refetchFunction, onClose, open}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [updateSystemMutation, {loading}] = useMutation(UpdateSystem);
    const [systemType, setSystemType] = useState<string | null>(null);
    const [title, setTitle] = useState('');

    const systemId = 'system' in node ? node.system?.id : undefined;

    const {data: systemData} = useQuery(SystemNameplateInfo, {
        variables: {id: systemId},
        skip: !systemId,
    });

    useEffect(() => {
        const systemTitle = systemData?.system?.title;
        if (systemTitle) setTitle(systemTitle);
    }, [systemData]);

    // Network scan gate (IBM-1039): choosing the gateway type is the first step
    // of attaching a connection, so a blocked scan replaces the tiles with the
    // reason. Not sent while the flag is off.
    const iotGate = useIotAttachGate({kind: 'system', id: systemId});
    const scanBlocked = iotGate.reason === 'networkScan';

    // Current subtree structure (IBM-462): a target type is only selectable when the
    // structure fits its bounds. While the query is loading/unavailable nothing is
    // blocked — the backend rejects violations (SYSTEM_STRUCTURE_EXCEEDS_TARGET_LIMITS).
    const {data: structureData} = useQuery(SystemStructureCounts, {
        variables: {id: systemId},
        skip: !systemId,
    });
    const structureNode = structureData?.system?.installedBaseNode;
    const directPressureLines: number | undefined =
        getChildRule(structureNode?.nodeRules, InstalledBaseNodeTypeKey.PRESSURE_LINE)?.currentNodeCount;
    const subtreeAssets: number | undefined = structureNode?.assetCount ?? undefined;

    /** Reason the type can't be selected, or null when it fits (or structure is unknown). */
    const getTypeRestriction = (type: typeof GATEWAY_SYSTEM_TYPES[number]): string | null => {
        if (!structureNode) return null;
        if (directPressureLines != null && directPressureLines > type.maxPressureLines) {
            return t('Exceeds max. {{max}} pressure lines', {max: type.maxPressureLines});
        }
        if (subtreeAssets != null && subtreeAssets > type.maxAssets) {
            return t('Exceeds max. {{max}} assets', {max: type.maxAssets});
        }
        return null;
    };
    const noTypeAvailable = GATEWAY_SYSTEM_TYPES.every((type) => getTypeRestriction(type) !== null);

    const gatewayLabels = GATEWAY_SYSTEM_TYPES.map((type) => t(type.label));
    const gatewayList = [gatewayLabels.slice(0, -1).join(", "), gatewayLabels[gatewayLabels.length - 1]]
        .filter(Boolean)
        .join(`, ${t("or")} `);

    const handleTypeChange = (
        event: React.MouseEvent<HTMLElement>,
        newType: string | null,
    ) => {
        setSystemType(newType);
    };

    const handleConnect = () => {
        if (!systemType) {
            toast.error(t("Please select the system type."))
            return
        }

        if (!systemId) {
            toast.error(t("No system found for this node."))
            return
        }
        updateSystemMutation({
            variables: {
                systemId,
                input: {systemType, title: title || null}
            },
            refetchQueries: ['SystemIotConnection', 'SystemNameplateInfo', 'SystemIoTConnectionStatus'],
            awaitRefetchQueries: true,
        }).then((res) => {
            const id = res.data?.updateSystem?.id
            toast.success(t("System has been connected successfully."))
            if (refetchFunction) refetchFunction()
            if (onClose) onClose()
            navigate(`/systems/${id}/dashboard`)
        }).catch((err) => {
            toast.error(resolveErrorMessage(err, t))
        })
    }

    // Network scan gate: replaces the modal content while the gate is loading,
    // failed or blocks the attach.
    if (iotGate.pending || iotGate.error || scanBlocked) {
        return <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                title={<Typography level={'titleSmallLight'}>{t('Connect IoT system')}</Typography>}
                onClose={onClose}
            >
                <Box sx={{pt: "16px"}}>
                    {iotGate.pending ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: "32px"}}>
                            <CircularProgress size={28}/>
                        </Box>
                    ) : (
                        <>
                            <Box
                                borderRadius="4px"
                                border="1px solid #ccc"
                                padding="12px 16px"
                                data-testid={scanBlocked ? "system-connect-network-scan" : undefined}
                            >
                                {iotGate.error ? (
                                    <Typography level={"body"}>
                                        {t('The connection form could not be loaded. Please try again.')}
                                    </Typography>
                                ) : (
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                        <Typography level={"body"}>
                                            {t('An IoT connection can only be set up once a passed network scan is documented for this system or one of its parent elements.')}
                                        </Typography>
                                        <NetworkScanGateReason
                                            scan={iotGate.scan}
                                            ownNodeId={iotGate.ownNodeId ?? node.id}
                                            pathNodeIds={iotGate.pathNodeIds}
                                            align="start"
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{pt: '24px'}}>
                                <Button
                                    fullWidth
                                    variant='outlined'
                                    onClick={onClose}
                                >
                                    {t("Close")}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </BasicModalContent>
        </Dialog>;
    }

    return <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
        <BasicModalContent
            title={<Typography level={'titleSmallLight'}>{t('Connect IoT system')}</Typography>}
            onClose={onClose}
        >
            <Box sx={{pt: "16px"}}>
                <Box
                    borderRadius="4px"
                    border="1px solid #ccc"
                    padding="12px 16px"
                    marginBottom="20px"
                >
                    <Typography level={"body"} fontWeight={"bold"}>
                        {t("Please select the type for your system.")}
                    </Typography>
                    <Typography level={"body"} sx={{pt: "8px"}}>
                        {t("Select one of the available gateways")} <strong>({gatewayList})</strong> {t("to enable system connectivity and data exchange.")}
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    value={systemType}
                    exclusive
                    onChange={handleTypeChange}
                    size={"large"}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "8px",
                        "& .MuiToggleButtonGroup-grouped": {
                            margin: 0,
                        },
                    }}
                >
                    {GATEWAY_SYSTEM_TYPES.map((type) => {
                        const restriction = getTypeRestriction(type);
                        return (
                            <ToggleButton
                                value={type.value}
                                aria-label={type.label}
                                sx={{
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                    textTransform: "none",
                                    padding: "12px 16px",
                                    border: "1px solid #ccc !important",
                                    borderRadius: "4px !important",
                                }}
                                key={type.value}
                                disabled={restriction !== null}
                            >
                                <Box sx={{display: "flex", flexDirection: "row", alignItems: "center", gap: "16px"}}>
                                    <img
                                        src={type.image}
                                        alt={type.label}
                                        style={{
                                            width: 64,
                                            height: 64,
                                            objectFit: "contain",
                                            borderRadius: 4,
                                            ...(restriction !== null ? {opacity: 0.5} : {}),
                                        }}
                                    />
                                    <Box sx={{display: "flex", flexDirection: "column", alignItems: "flex-start"}}>
                                        <Typography>{t(type.label)}</Typography>
                                        <Typography level={"bodySmall"} sx={{color: "#666"}}>
                                            {t("System type {{number}}", {number: type.number})}
                                        </Typography>
                                        {restriction !== null && (
                                            <Typography level={"bodySmall"} sx={{pt: "2px"}}>
                                                {restriction}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>

                {noTypeAvailable && (
                    <Box
                        borderRadius="4px"
                        border="1px solid #ccc"
                        padding="12px 16px"
                        marginTop="16px"
                    >
                        <Typography level={"body"}>
                            {t('The current structure of this system ({{pressureLines}} pressure lines, {{assets}} assets) exceeds the limits of all available system types.', {
                                pressureLines: directPressureLines ?? 0,
                                assets: subtreeAssets ?? 0,
                            })}
                        </Typography>
                    </Box>
                )}

                {systemType && (
                    <Box sx={{pt: "16px"}}>
                        <TextField
                            label={t("Title")}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            fullWidth
                            required
                            error={!title.trim()}
                            helperText={!title.trim() ? t("Title is required.") : undefined}
                        />
                    </Box>
                )}

                <Grid container spacing={1} sx={{pt: '24px'}}>
                    <Grid xs={6}>
                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={onClose}
                        >
                            {t("Cancel")}
                        </Button>
                    </Grid>
                    <Grid xs={6}>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!systemType || !title.trim() || loading}
                            onClick={handleConnect}
                        >
                            {t("Connect")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </BasicModalContent>
    </Dialog>
};

export default IotSystemRegistrationModal;
